import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api, ErroApi } from '../api';
import { Pendencias } from '../Pendencias';
import {
  adotarIsc, descartarIscCorpo, descartarIscPenetracao, gravarIscCabecalho,
  gravarIscCorpos, gravarIscExpansao, gravarIscPenetracao, verIsc,
} from '../api/isc';
import type { EnsaioIsc } from '../api/isc';
import {
  AncoraDePendencias, BlocoDaFicha, CabecalhoDaFicha, EtapasDaFicha,
  FaixaDaFicha, JanelaDeDescarte, LaudoDaFicha, RodapeDeEtapas,
  fichaCongelada, useEquipamentos, useEtapa, useFicha,
} from '../Ficha';
import type { Etapa, Indicador } from '../Ficha';
import { numero, paraNumero, paraCampo, plural, rotulo } from '../formato';
import { tecladoDaTabela } from '../tecladoDaTabela';
import { FotosDoRegistro } from '../FotosDoRegistro';
import { Plano, Segmento } from '../Grafico';
import {
  Aviso, Campo, CampoNumerico, Carregando, ErroDaApi, Escolha,
  RespostaDaAcao, Selo, TabelaDeLancamento,
} from '../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../ui';
import { useSessao } from '../sessao';
import { AvisoDaRepeticaoAberta } from './Triviais';
import { useLinhasDaRepeticao } from './repeticao/Repeticao';
import { CorposLadoALado, RepeticaoIsc } from './repeticao/RepeticaoIsc';
import { TentativasAnteriores } from './repeticao/TentativasAnteriores';
import { TrilhaDoEnsaio } from './repeticao/TrilhaDoEnsaio';

/**
 * Lançar um ISC — da imersão ao PDF (ABNT NBR 9895:2016).
 *
 * ─── No Carbon (onda 237) ──────────────────────────────────────────────
 *
 * Quatro etapas: condições, corpos de prova, ISC adotado, fotos e laudo.
 * Na etapa dos corpos de prova escolhe-se o CP, e a penetração, a
 * expansão e a curva PRESSÃO × PENETRAÇÃO dele aparecem juntas (decisão 3
 * do Humberto, 02/10/2026). A curva é a das leituras, ligadas ponto a
 * ponto, com as verticais de 2,54 e 5,08 mm (deslocadas da correção de
 * origem, quando houve) — a tela não ajusta nada.
 *
 * O ISC não tem balança: os corpos de prova são pontos da compactação,
 * pesados lá (RN-215, 0217), e as leituras daqui são de extensômetro e de
 * carga.
 *
 * As casas: a NBR 9895:2016 não fixa o arredondamento do ISC nem o da
 * expansão (`PD-24`), e a ficha segue o que o laudo já imprime — ISC com
 * duas casas e expansão com três.
 *
 * ─── A tela não molda corpo de prova ───────────────────────────────────
 *
 * Os corpos de prova de ISC são PONTOS de uma compactação que já existe,
 * cada um com γd e w médio calculados pela `0022`. Repetir a moldagem
 * aqui criaria um segundo γd para o mesmo cilindro.
 *
 * ─── O botão que a tela NÃO tem ────────────────────────────────────────
 *
 * «Calcular o ISC do ensaio». A NBR 9895, 6.4.2, manda obter o valor da
 * curva segundo critérios de PROJETO, e não diz qual é o critério. A tela
 * mostra o ISC de cada corpo de prova e o interpolado na umidade ótima
 * como apoio, e para gravar o valor do ensaio pede as três coisas juntas:
 * valor, critério por escrito e quem assina (`PD-58`).
 */

const CAT_ANEL = ['anel_dinamometrico', 'anel', 'celula_carga'];

/** A 9895 não fixa (PD-24): as casas são as que o laudo já imprime. */
export const CASAS = { isc: 2, expansao: 3, pressao: 3 } as const;

const num = (v: unknown, casas: number) => numero(v, casas);

export function Isc() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioIsc | null>(null);
  const equip = useEquipamentos();
  const [gente, setGente] = useState<{ id: string; nome: string }[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const ficha = useFicha(id, 'NBR 9895', versao);
  /* R2 (onda 323): com a repetição aberta, a etapa dos corpos de prova lista
     e lança as leituras DELA (os mesmos CPs, copiados na abertura — D10). */
  const rep = useLinhasDaRepeticao<EnsaioIsc>(id, 'isc', d?.repeticaoAberta, versao);
  const { pode } = useSessao();
  /* A Interpretação (onda 323) existe só para quem interpreta. */
  const interpreta = pode('ensaio.interpretar');
  const total = interpreta ? 5 : 4;

  const carregar = useCallback(async () => {
    try { setD(await verIsc(id)); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  useEffect(() => {
    void (async () => {
      try {
        const u = await api<{ id: string; nome: string }[]>('/usuarios');
        setGente(Array.isArray(u) ? u : []);
      } catch { /* o seletor cai vazio, e a tela diz isso */ }
    })();
  }, []);

  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<EnsaioIsc>,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      setD(await chamada());
      setOk('Gravado.');
      setVersao((v) => v + 1);
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, []);

  const opcoes = (cats: string[]): Opcao[] => equip
    .filter((e) => cats.includes(e.categoria))
    .map((e) => ({ valor: e.id, texto: e.codigo_patrimonial,
      detalhe: e.nome }));

  const campoErro = (c: string) =>
    erro instanceof ErroApi ? erro.campo(c) : undefined;

  const c = d?.cabecalho;
  /* O que a etapa dos corpos de prova mostra: a repetição aberta, se houver
     (R2). A faixa, o ISC adotado e o laudo continuam com a vigente (D5). */
  const repAberta = d?.repeticaoAberta != null;
  const daBancada: EnsaioIsc | null = d && repAberta && rep.atual
    ? { ...d, corpos: rep.atual.corpos ?? [], expansao: rep.atual.expansao ?? [],
        penetracao: rep.atual.penetracao ?? [],
        pontosDisponiveis: rep.atual.pontosDisponiveis ?? [] }
    : d;
  const vivos = (d?.corpos ?? []).filter((x: any) => !x.descartado);
  const comPenetracao = vivos.filter((x: any) => x.isc != null).length;
  const bVivos = (daBancada?.corpos ?? []).filter((x: any) => !x.descartado);
  const bComPenetracao = bVivos.filter((x: any) => x.isc != null).length;
  const temCondicoes = c != null;
  const temCorpos = vivos.length > 0 && comPenetracao === vivos.length;
  const temAdotado = c?.isc_adotado != null;
  const [etapa, irPara] = useEtapa(total,
    !temCondicoes ? 1 : !temCorpos ? 2 : !temAdotado ? 3 : total);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d || !daBancada) return <Carregando texto="Abrindo o ensaio" />;
  const congelado = fichaCongelada(ficha?.status);
  const naTentativa = repAberta ? ` · tentativa ${d.repeticaoAberta}` : '';

  const etapas: Etapa[] = [
    { nome: 'Condições', feita: temCondicoes,
      resumo: temCondicoes
        ? [c?.sobrecarga_g != null ? `${num(c.sobrecarga_g, 0)} g` : null,
          c?.dias_imersao != null ? plural(Number(c.dias_imersao), 'dia', 'dias') : null,
          c?.velocidade_mm_min != null ? `${num(c.velocidade_mm_min, 2)} mm/min` : null]
          .filter(Boolean).join(' · ')
        : 'a gravar' },
    { nome: 'Corpos de prova',
      feita: repAberta ? bVivos.length > 0 && bComPenetracao === bVivos.length : temCorpos,
      resumo: (bVivos.length
        ? `${bComPenetracao} de ${bVivos.length} com penetração` : 'nenhum ainda') + naTentativa },
    { nome: 'ISC adotado', feita: temAdotado, falta: temCorpos && !temAdotado,
      resumo: temAdotado ? `${num(c.isc_adotado, CASAS.isc)} %` : 'falta adotar' },
    ...(interpreta ? [{
      nome: 'Interpretação', feita: temCorpos && !repAberta,
      resumo: repAberta ? `repetindo · tentativa ${d.repeticaoAberta}`
        : vivos.length ? plural(vivos.length, 'corpo de prova', 'corpos de prova') : 'depois dos CPs',
    }] : []),
    { nome: 'Fotos e laudo', feita: ficha?.status === 'aprovado' },
  ];

  const indicadores: Indicador[] = [
    { rotulo: 'ISC do ensaio', chave: true, unidade: '%',
      valor: c?.isc_adotado != null ? num(c.isc_adotado, CASAS.isc) : null,
      porque: 'Decisão de projeto — adotado na etapa 3, por quem assina.',
      como: <>NBR 9895:2016, 6.4.2: o valor do ensaio é obtido da curva
        segundo critérios de projeto. O sistema não escolhe — quem adota
        escreve o critério e assina.</> },
    { rotulo: 'ISC na umidade ótima', unidade: '%',
      valor: c?.isc_na_umidade_otima != null
        ? num(c.isc_na_umidade_otima, CASAS.isc) : null,
      como: 'Interpolado na w ótima da compactação de origem — apoio para a adoção, não o resultado.' },
    { rotulo: 'Expansão máxima', unidade: '%',
      valor: c?.expansao_maxima != null ? num(c.expansao_maxima, CASAS.expansao) : null },
    { rotulo: 'Corpos de prova', valor: String(c?.corpos_prova ?? vivos.length) },
    { rotulo: 'Imersão', unidade: 'dias',
      valor: c?.dias_imersao != null ? String(c.dias_imersao) : null },
  ];

  return (
    <div className="tela isc ficha-carbon">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'ISC'}
        titulo="ISC e expansão" ficha={ficha} normaDaTela="ABNT NBR 9895"
        tentativaAberta={d?.repeticaoAberta} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />
      <AncoraDePendencias>
        <Pendencias itens={d.pendencias} id={id}
          detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
      </AncoraDePendencias>
      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(ficha?.status).toLowerCase()} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}

      {repAberta && etapa === 2 && (
        <AvisoDaRepeticaoAberta numero={d.repeticaoAberta!} interpreta={interpreta} />
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <Condicoes
          c={c} id={id} gravar={gravar} salvando={salvando}
          campoErro={campoErro} opcoesAnel={opcoes(CAT_ANEL)}
          compactacoes={d.compactacoes} />
      )}
      {etapa === 2 && repAberta && rep.carregando && (
        <Carregando texto="Lendo os corpos de prova da repetição" />
      )}
      {etapa === 2 && !(repAberta && rep.carregando) && (
        <Corpos key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
          d={daBancada} id={id} gravar={gravar} salvando={salvando} />
      )}
      {etapa === 3 && (
        <Resultado d={d} id={id} gravar={gravar} salvando={salvando}
          campoErro={campoErro} gente={gente} />
      )}
      {interpreta && etapa === 4 && (
        <>
          {/* A repetição na Interpretação (onda 323, RN-260, D10: os mesmos CPs). */}
          <RepeticaoIsc id={id} congelado={congelado}
            aoMudar={async () => { await carregar(); setVersao((v) => v + 1); }}>
            <CorposLadoALado inicial={d} atual={null} />
          </RepeticaoIsc>
          <TentativasAnteriores ensaioId={id} ficha="isc" versao={versao}
            render={(r: any) => <CorposLadoALado inicial={r} atual={null} />} />
          <TrilhaDoEnsaio ensaioId={id} versao={versao} />
        </>
      )}

      {etapa === total && (
        <>
          <FotosDoRegistro
            id={id}
            titulo="Registro fotográfico"
            dica={'O corpo de prova antes da imersão e depois da penetração.'}
            roteiro={[
              { tipo: 'cp_antes', nome: 'CP moldado', quando: 'antes da imersão' },
              { tipo: 'cp_depois', nome: 'CP após a penetração', quando: 'no fim' },
            ]}
          />
          <LaudoDaFicha id={id} />
        </>
      )}
      </fieldset>

      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
    </div>
  );
}

// ── 1 · condições ─────────────────────────────────────────────────────

export interface GravarFn {
  (marca: string, chamada: () => Promise<EnsaioIsc>): Promise<boolean>;
}

function Condicoes({
  c, id, gravar, salvando, campoErro, opcoesAnel, compactacoes,
}: {
  c: any; id: string; gravar: GravarFn; salvando: string | null;
  campoErro(x: string): string | undefined; opcoesAnel: Opcao[];
  compactacoes: any[];
}) {
  const [origem, setOrigem] = useState<string | null>(null);
  const [sobrecarga, setSobrecarga] = useState('');
  const [dias, setDias] = useState('');
  const [velocidade, setVelocidade] = useState('');
  const [pistao, setPistao] = useState('');
  const [anel, setAnel] = useState<string | null>(null);
  const [somenteNorma, setSomenteNorma] = useState(true);

  useEffect(() => {
    setOrigem(c?.compactacao_ensaio_id ?? null);
    setSobrecarga(c?.sobrecarga_g != null ? paraCampo(c.sobrecarga_g) : '4540');
    setDias(c?.dias_imersao != null ? paraCampo(c.dias_imersao) : '');
    setVelocidade(c?.velocidade_mm_min != null
      ? paraCampo(c.velocidade_mm_min) : '1,27');
    setPistao(c?.diametro_pistao_mm != null
      ? paraCampo(c.diametro_pistao_mm) : '49,6');
    setAnel(c?.anel_equipamento_id ?? null);
    setSomenteNorma(c?.ajuste_somente_norma ?? true);
  }, [c?.compactacao_ensaio_id, c?.sobrecarga_g, c?.dias_imersao,
    c?.velocidade_mm_min, c?.diametro_pistao_mm, c?.anel_equipamento_id,
    c?.ajuste_somente_norma]);

  const diasNum = Number(paraNumero(dias) ?? 0);
  const falta = !origem ? 'Falta escolher a compactação de origem.' : null;

  return (
    <BlocoDaFicha titulo="Condições do ensaio"
      sub="Os corpos de prova do ISC são os pontos da compactação de origem.">
      <div className="fe-miolo fe-campos">
        <Escolha
          rotulo="Compactação de origem" valor={origem} aoEscolher={setOrigem}
          limpavel={false}
          opcoes={compactacoes.map((k: any) => ({
            valor: k.ensaio_id,
            texto: `${k.energia ?? '—'} · cilindro ${k.cilindro ?? '—'}`
              + (k.umidade_otima != null
                ? ` · w ót. ${num(k.umidade_otima, 1)} %` : ''),
          }))}
          erro={campoErro('compactacaoEnsaioId')}
        />
        <Campo rotulo="Sobrecarga (g)" inputMode="decimal" value={sobrecarga}
          onChange={(e) => setSobrecarga(e.target.value)}
          erro={campoErro('sobrecargaG')}
          dica="A NBR 9895, 5.3.2, prevê (4 540 ± 20) g." />
        <Campo rotulo="Dias de imersão" inputMode="numeric" value={dias}
          onChange={(e) => setDias(e.target.value)}
          erro={campoErro('diasImersao')}
          dica="A NBR 9895, 5.3.3, pede no mínimo quatro." />
        <Campo rotulo="Velocidade de penetração (mm/min)" inputMode="decimal"
          value={velocidade} onChange={(e) => setVelocidade(e.target.value)}
          erro={campoErro('velocidadeMmMin')}
          dica="A NBR 9895, 5.4.3, prevê 1,27 mm/min." />
        <Campo rotulo="Diâmetro do pistão (mm)" inputMode="decimal"
          value={pistao} onChange={(e) => setPistao(e.target.value)}
          erro={campoErro('diametroPistaoMm')}
          dica="Figura A.6: Ø 49,6 mm. É o que divide a carga para dar a pressão." />
        <Escolha rotulo="Anel dinamométrico / célula" valor={anel}
          aoEscolher={setAnel} opcoes={opcoesAnel}
          dica="Só é preciso quando a carga entra como deflexão do anel." />
        <label className="marcavel">
          <input type="checkbox" checked={somenteNorma}
            onChange={(e) => setSomenteNorma(e.target.checked)} />
          Ajustar a cúbica somente sobre as penetrações da Tabela 3
        </label>
      </div>
      <div className="fe-miolo">
        {compactacoes.length === 0 && (
          <Aviso tipo="atencao" titulo="Esta amostra não tem compactação">
            O ISC pendura numa compactação: é dela que vêm γd e w médio de
            cada corpo de prova. Lance a compactação primeiro.
          </Aviso>
        )}
        {dias !== '' && diasNum > 0 && diasNum < 4 && (
          <Aviso tipo="atencao" titulo="Imersão abaixo da norma">
            A NBR 9895, 5.3.3, pede no mínimo quatro dias. O sistema aceita e
            o laudo diz — a decisão de emitir assim fica registrada.
          </Aviso>
        )}
      </div>
      <div className="acoes fe-acoes">
        {falta && <p className="o-que-falta">{falta}</p>}
        <button type="button" className="principal"
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? undefined}
          onClick={() => {
            if (falta || salvando !== null) return;
            void gravar('cabecalho', () =>
              gravarIscCabecalho(id, {
                compactacaoEnsaioId: origem,
                sobrecargaG: paraNumero(sobrecarga),
                diasImersao: dias === '' ? undefined
                  : Number(paraNumero(dias) ?? 0),
                velocidadeMmMin: paraNumero(velocidade),
                diametroPistaoMm: paraNumero(pistao),
                anelEquipamentoId: anel ?? undefined,
                ajusteSomenteNorma: somenteNorma,
              }));
          }}>
          {salvando === 'cabecalho' ? 'Gravando…' : 'Gravar condições'}
        </button>
      </div>
    </BlocoDaFicha>
  );
}

// ── 2 · corpos de prova ───────────────────────────────────────────────

function Corpos({
  d, id, gravar, salvando,
}: {
  d: EnsaioIsc; id: string; gravar: GravarFn; salvando: string | null;
}) {
  const [escolhidos, setEscolhidos] = useState<Record<string, boolean>>({});
  const [trazendo, setTrazendo] = useState(d.corpos.length === 0);
  const [cpId, setCpId] = useState<string | null>(null);
  const [descartando, setDescartando] = useState<any | null>(null);

  const marcados = Object.entries(escolhidos).filter(([, v]) => v).map(([k]) => k);
  const cp = d.corpos.find((x: any) => x.id === cpId)
    ?? d.corpos.find((x: any) => !x.descartado) ?? d.corpos[0] ?? null;
  const vivos = d.corpos.filter((x: any) => !x.descartado).length;

  return (
    <BlocoDaFicha titulo="Corpos de prova"
      selo={d.corpos.length ? <Selo tom={vivos ? 'ok' : 'atencao'}>
        {plural(vivos, 'válido', 'válidos')}</Selo> : undefined}
      sub="Os corpos de prova são pontos da compactação: γd e w vêm de lá. Escolha o CP para lançar a expansão e a penetração dele.">
      <div className="fe-miolo fe-cps">
        {d.corpos.length > 0 && (
          <div className="fe-alternador" role="group" aria-label="Corpo de prova">
            {d.corpos.map((x: any) => (
              <button key={x.id} type="button" aria-pressed={cp?.id === x.id}
                className={x.descartado ? 'descartado' : undefined}
                onClick={() => setCpId(x.id)}>
                CP {x.ordem}
                <small>
                  {x.descartado ? 'descartado'
                    : `w ${num(x.umidade_media, 1)} % · ISC ${x.isc == null ? '—' : `${num(x.isc, CASAS.isc)} %`}`}
                </small>
              </button>
            ))}
          </div>
        )}
        <button type="button" className="botao discreto"
          aria-expanded={trazendo} onClick={() => setTrazendo((v) => !v)}>
          + Trazer pontos da compactação
        </button>
      </div>

      {trazendo && (
        <div className="fe-miolo">
          {d.pontosDisponiveis.length === 0
            ? (
              <p className="sub">
                {d.cabecalho
                  ? 'Todos os pontos daquela compactação já viraram corpo de prova.'
                  : 'Escolha a compactação de origem na etapa 1 primeiro.'}
              </p>
            )
            : (
              <>
                <table className="dados fe-lanc">
                  <caption className="so-leitor">Pontos da compactação disponíveis</caption>
                  <thead>
                    <tr><th></th><th>Ponto</th><th>Cilindro</th>
                      <th className="num">w médio (%)</th><th className="num">γd (g/cm³)</th></tr>
                  </thead>
                  <tbody>
                    {d.pontosDisponiveis.map((p: any) => (
                      <tr key={p.id}>
                        <td>
                          <input type="checkbox" aria-label={`Trazer o ponto ${p.ordem}`}
                            checked={Boolean(escolhidos[p.id])}
                            onChange={(e) => setEscolhidos(
                              (m) => ({ ...m, [p.id]: e.target.checked }))} />
                        </td>
                        <td>{p.ordem}</td>
                        <td className="cod">{p.cilindro}</td>
                        <td className="num">{num(p.umidade_media, 1)}</td>
                        <td className="num">{num(p.massa_especifica_seca, 3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="acoes fe-acoes">
                  <button type="button" className="principal"
                    aria-disabled={!marcados.length || salvando !== null}
                    title={!marcados.length ? 'Marque os pontos que viram corpo de prova.' : undefined}
                    onClick={async () => {
                      if (!marcados.length || salvando !== null) return;
                      const feito = await gravar('corpos', () =>
                        gravarIscCorpos(id, {
                          corpos: marcados.map((pid) => ({ pontoId: pid })),
                        }));
                      if (feito) { setEscolhidos({}); setTrazendo(false); }
                    }}>
                    Trazer {marcados.length || ''} corpo(s) de prova
                  </button>
                </div>
              </>
            )}
        </div>
      )}

      {cp && (
        <>
          <div className="fe-miolo fe-cp-topo">
            <span>
              <b>CP {cp.ordem}</b> · cilindro <span className="cod">{cp.cilindro}</span>
              {' '}· w {num(cp.umidade_media, 1)} % · γd {num(cp.massa_especifica_seca, 3)} g/cm³
            </span>
            {cp.altura_inicial_mm == null && <Selo tom="critico">Molde sem altura calibrada</Selo>}
            {cp.leituras_decrescentes && <Selo tom="atencao">Carga decrescente</Selo>}
            {cp.descartado
              ? <Selo tom="neutro" titulo={cp.motivo_descarte}>Descartado</Selo>
              : (
                <button type="button" className="botao discreto pequeno fe-descartar"
                  disabled={salvando !== null}
                  onClick={() => setDescartando(cp)}>
                  Descartar o CP {cp.ordem}
                </button>
              )}
          </div>
          {cp.corrigido === false && cp.correcao_motivo && (
            <p className="sub fe-miolo">
              Origem não corrigida (NBR 9895, 5.5.3.2): {cp.correcao_motivo}
            </p>
          )}
          {cp.corrigido === true && (
            <p className="sub fe-miolo">
              Origem corrigida em {num(cp.correcao, 3)} mm · inflexão em{' '}
              {num(cp.inflexao, 2)} mm
            </p>
          )}

          <Penetracao cp={cp}
            linhas={d.penetracao.filter((l: any) => l.corpo_prova_id === cp.id)}
            tabela={d.tabelaNorma} id={id} gravar={gravar} salvando={salvando} />

          <div className="fe-lado">
            <Expansao cp={cp}
              linhas={d.expansao.filter((l: any) => l.corpo_prova_id === cp.id)}
              id={id} gravar={gravar} salvando={salvando} />
            <CurvaPressao cp={cp}
              linhas={d.penetracao.filter((l: any) => l.corpo_prova_id === cp.id)} />
          </div>
        </>
      )}

      <JanelaDeDescarte
        alvo={descartando ? `o CP ${descartando.ordem}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar('descarte',
          () => descartarIscCorpo(id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </BlocoDaFicha>
  );
}

/**
 * As leituras de penetração do CP.
 *
 * As 14 penetrações da Tabela 3 vêm do SERVIDOR num seletor — digitar
 * «2,54» à mão catorze vezes por corpo de prova é como se produz um 2,45.
 *
 * Carga OU deflexão do anel: os dois campos existem, e o botão só liga
 * quando exatamente um está preenchido. A NBR 9895, 5.4.4, admite os dois
 * jeitos; o banco recusa os dois ao mesmo tempo.
 */
export function Penetracao({
  cp, linhas, tabela, id, gravar, salvando, secundario,
}: {
  cp: any; linhas: any[]; tabela: any[]; id: string; gravar: GravarFn;
  salvando: string | null;
  /** Na Interpretação (onda 323) o primário da tela é «Adotar a repetição». */
  secundario?: boolean;
}) {
  const [pen, setPen] = useState('');
  const [carga, setCarga] = useState('');
  const [anel, setAnel] = useState('');
  const [descartando, setDescartando] = useState<any | null>(null);
  useEffect(() => { setPen(''); setCarga(''); setAnel(''); }, [cp.id]);

  const usadas = new Set(linhas.filter((l: any) => !l.descartada)
    .map((l: any) => String(Number(l.penetracao_mm))));
  const tempoDe = (mm: unknown) => tabela.find(
    (t: any) => Number(t.penetracao_mm) === Number(mm))?.tempo_min;
  /* A próxima da Tabela 3 que ainda não tem leitura vem escolhida. */
  const proxima = tabela.find((t: any) => !usadas.has(String(Number(t.penetracao_mm))));
  const penEscolhida = pen || (proxima ? String(proxima.penetracao_mm) : '');
  const umSo = (carga !== '') !== (anel !== '');
  const falta = !penEscolhida ? 'Todas as penetrações da Tabela 3 já têm leitura.'
    : carga === '' && anel === '' ? 'Informe a carga OU a deflexão do anel.'
      : !umSo ? 'Uma das duas: a carga em newton OU a deflexão do anel.' : null;

  const colunas: ColunaDeLancamento[] = [
    { texto: 'Penetração (mm)', num: true },
    { texto: 'Tempo (min)', num: true },
    { texto: 'Carga (N)', num: true },
    { texto: 'Anel (mm)', num: true },
    { texto: 'Pressão (MPa)', num: true },
    { texto: 'Norma' },
    { texto: '' },
  ];
  const ordenadas = [...linhas].sort(
    (a: any, b: any) => Number(a.penetracao_mm) - Number(b.penetracao_mm));
  const lancadas: LinhaDeLancamento[] = ordenadas.map((l: any) => {
    const marco = Number(l.penetracao_mm) === 2.54 ? cp.isc_254
      : Number(l.penetracao_mm) === 5.08 ? cp.isc_508 : null;
    return {
      chave: l.id,
      classe: l.descartada ? 'descartada' : undefined,
      titulo: <>{num(l.penetracao_mm, 2)} mm</>,
      resultado: l.descartada ? '—' : <span className="n">{num(l.pressao_mpa, CASAS.pressao)} MPa</span>,
      nota: l.descartada ? `Descartada: ${l.motivo_descarte ?? ''}` : undefined,
      semCartao: [0, 4],
      celulas: [
        num(l.penetracao_mm, 2),
        num(tempoDe(l.penetracao_mm), 1),
        l.carga_resolvida_n == null ? <Selo tom="critico">Sem carga</Selo>
          : num(l.carga_resolvida_n, 1),
        l.leitura_anel_mm == null ? <span className="sub">—</span> : num(l.leitura_anel_mm, 4),
        <b className="fe-w">{num(l.pressao_mpa, CASAS.pressao)}</b>,
        marco != null && !l.descartada
          ? <Selo tom="ok">ISC {num(marco, CASAS.isc)} %</Selo>
          : <Selo tom="neutro">{l.penetracao_de_norma ? 'Tab. 3' : 'extra'}</Selo>,
        l.descartada
          ? <span className="fe-motivo" title={l.motivo_descarte}><Selo tom="neutro">Descartada</Selo></span>
          : (
            <button type="button" className="botao discreto pequeno fe-descartar"
              aria-label={`Descartar a leitura de ${num(l.penetracao_mm, 2)} mm`}
              disabled={salvando !== null}
              onClick={() => setDescartando(l)}>
              Descartar
            </button>
          ),
      ],
    };
  });
  const nova: LinhaDeLancamento[] = cp.descartado ? [] : [{
    chave: 'nova',
    classe: 'nova',
    titulo: 'Nova leitura',
    semCartao: [1, 4, 5],
    celulas: [
      <label className="campo">
        <span className="so-leitor">Penetração</span>
        <select value={penEscolhida} onChange={(e) => setPen(e.target.value)}>
          <option value="">penetração…</option>
          {tabela.map((t: any) => (
            <option key={t.penetracao_mm} value={t.penetracao_mm}
              disabled={usadas.has(String(Number(t.penetracao_mm)))}>
              {num(t.penetracao_mm, 2)} mm · {num(t.tempo_min, 1)} min
            </option>
          ))}
        </select>
      </label>,
      <span className="sub">{num(tempoDe(penEscolhida), 1) || '—'}</span>,
      <CampoNumerico rotulo="Carga (N)" rotuloOculto placeholder="carga (N)"
        value={carga} onChange={(e) => setCarga(e.target.value)} />,
      <CampoNumerico rotulo="Deflexão do anel (mm)" rotuloOculto placeholder="ou anel (mm)"
        value={anel} onChange={(e) => setAnel(e.target.value)} />,
      <span className="sub">—</span>,
      <span />,
      <span />,
    ],
  }];

  return (
    <div className="fe-tabela">
      <div className="fe-tabela-topo">
        <h3>Penetração · CP {cp.ordem} <small>{linhas.length
          ? plural(linhas.filter((l: any) => !l.descartada).length, 'leitura', 'leituras')
          : 'nenhuma leitura ainda'}</small></h3>
      </div>
      <TabelaDeLancamento rotulo={`Penetração do CP ${cp.ordem}`} classe="fe-lanc"
        colunas={colunas} linhas={[...lancadas, ...nova]} />
      {!cp.descartado && (
        <div className="acoes fe-acoes">
          {falta ? <p className="o-que-falta">{falta}</p>
            : <p className="fe-lembrete">A pressão é a carga sobre a área do pistão — calculada pelo banco.</p>}
          <button type="button" className={secundario ? 'botao secundario' : 'principal'}
            data-gravar-tabela=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={async () => {
              if (falta || salvando !== null) return;
              const feito = await gravar('penetracao', () =>
                gravarIscPenetracao(id, cp.id, {
                  leituras: [{
                    penetracaoMm: Number(penEscolhida),
                    cargaN: carga === '' ? undefined : paraNumero(carga),
                    leituraAnelMm: anel === '' ? undefined : paraNumero(anel),
                  }],
                }));
              if (feito) { setCarga(''); setAnel(''); setPen(''); }
            }}>
            {salvando === 'penetracao' ? 'Gravando…' : 'Gravar leitura'}
          </button>
        </div>
      )}
      <JanelaDeDescarte
        alvo={descartando ? `a leitura de ${num(descartando.penetracao_mm, 2)} mm` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar('descarte',
          () => descartarIscPenetracao(id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </div>
  );
}

/**
 * As leituras da imersão.
 *
 * A primeira linha é a de ordem 0 — a leitura ANTES de imergir. A tela
 * numera sozinha, e não deixa escolher: se a inicial pudesse ser
 * qualquer linha, a expansão da 5.5.2 dependeria de quem digitou.
 */
export function Expansao({
  cp, linhas, id, gravar, salvando,
}: {
  cp: any; linhas: any[]; id: string; gravar: GravarFn;
  salvando: string | null;
}) {
  const [leitura, setLeitura] = useState('');
  useEffect(() => { setLeitura(''); }, [cp.id]);
  const proxima = linhas.length
    ? Math.max(...linhas.map((l: any) => l.ordem)) + 1 : 0;

  return (
    <div className="bloco fe-bloco">
      <header className="fe-bloco-topo">
        <h2><span>Expansão · CP {cp.ordem}</span></h2>
        {cp.expansao_percent != null
          && <Selo tom="neutro">{num(cp.expansao_percent, CASAS.expansao)} %</Selo>}
      </header>
      <table className="dados fe-lanc"
        {...tecladoDaTabela(`Leituras do extensômetro do CP ${cp.ordem}`)}>
        <caption className="so-leitor">Leituras do extensômetro do CP {cp.ordem}</caption>
        <thead><tr><th>Leitura</th><th className="num">Extensômetro (mm)</th></tr></thead>
        <tbody>
          {[...linhas].sort((a: any, b: any) => a.ordem - b.ordem).map((l: any) => (
            <tr key={l.id}>
              <td>{l.ordem === 0 ? 'antes de imergir' : `${l.ordem}ª`}</td>
              <td className="num">{num(l.leitura_mm, 3)}</td>
            </tr>
          ))}
          {!cp.descartado && (
            <tr className="nova">
              <td>{proxima === 0 ? 'antes de imergir' : `${proxima}ª`}</td>
              <td className="num">
                <CampoNumerico rotulo={proxima === 0
                  ? 'Leitura antes de imergir (mm)' : `Leitura ${proxima} (mm)`} rotuloOculto
                  value={leitura} onChange={(e) => setLeitura(e.target.value)} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!cp.descartado && (
        <div className="acoes fe-acoes">
          <p className="fe-lembrete">A expansão é a variação sobre a altura calibrada do molde (5.5.2).</p>
          <button type="button" className="botao secundario" data-gravar-tabela=""
            aria-disabled={!leitura || salvando !== null}
            title={!leitura ? 'Digite a leitura do extensômetro.' : undefined}
            onClick={async () => {
              if (!leitura || salvando !== null) return;
              const feito = await gravar('expansao', () =>
                gravarIscExpansao(id, cp.id, {
                  leituras: [{ ordem: proxima, leituraMm: paraNumero(leitura) }],
                }));
              if (feito) setLeitura('');
            }}>
            {salvando === 'expansao' ? 'Gravando…' : `Gravar leitura ${proxima}`}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Pressão × penetração do CP, na tela (onda 237, decisão 3).
 *
 * As leituras ligadas ponto a ponto, e as verticais de 2,54 e 5,08 mm —
 * deslocadas de `c` quando a origem foi corrigida (5.5.3.2). É a leitura
 * que o técnico faz de olho para ver se a curva tem inflexão; o ajuste
 * da cúbica e a correção são do banco (0023) e não se refazem aqui.
 */
const pontosDaPressao = (linhas: any[]) => linhas
  .filter((l: any) => !l.descartada && l.pressao_mpa != null)
  .map((l: any) => ({ x: Number(l.penetracao_mm), y: Number(l.pressao_mpa) }))
  .sort((a, b) => a.x - b.x);

const traco = (pts: { x: number; y: number }[], px: (p: { x: number; y: number }) => { x: number; y: number }) =>
  pts.map((p, i) => {
    const q = px(p);
    return `${i ? 'L' : 'M'}${q.x.toFixed(2)} ${q.y.toFixed(2)}`;
  }).join(' ');

export function CurvaPressao({ cp, linhas, inicial, repeticao, titulo, acao }: {
  cp: any; linhas: any[];
  /** Na repetição (onda 323): a curva atual em azul e `data-serie="atual"`,
      com a inicial à mostra ou não. */
  repeticao?: boolean;
  titulo?: string;
  /**
   * O mesmo CP na tentativa inicial, na repetição do ISC (onda 323): a curva
   * tracejada e os pontos vazados — os pontos ganham `data-serie`.
   */
  inicial?: { cp: any; linhas: any[] } | null;
  /** Um controle no topo do bloco («mostrar a inicial»). */
  acao?: ReactNode;
}) {
  const pts = pontosDaPressao(linhas);
  const ini = inicial ? pontosDaPressao(inicial.linhas) : [];
  const todos = [...pts, ...ini];
  if (todos.length < 2) {
    return (
      <div className="bloco fe-bloco fe-grafico">
        <header className="fe-bloco-topo"><h2><span>{titulo ?? 'Pressão × penetração'}</span></h2>{acao}</header>
        <p className="sub">A curva aparece com duas leituras.</p>
      </div>
    );
  }
  const c = cp.corrigido === true && cp.correcao != null ? Number(cp.correcao) : 0;
  const xmax = Math.max(...todos.map((p) => p.x), 5.08 + c) * 1.08;
  const ymax = Math.max(...todos.map((p) => p.y)) * 1.15;
  const janela = { x0: 0, x1: xmax, y0: 0, y1: ymax };
  const marca = repeticao || inicial ? 'atual' : undefined;
  const nome = titulo ?? `Pressão × penetração · CP ${cp.ordem}${inicial ? ' · inicial × atual' : ''}`;
  return (
    <div className="bloco fe-bloco fe-grafico">
      <header className="fe-bloco-topo">
        <h2><span>{nome}</span></h2>
        {acao}
      </header>
      <div className="fe-miolo">
        <Plano janela={janela} tituloX="Penetração (mm)" tituloY="Pressão (MPa)">
          {({ px }) => (
            <>
              {[2.54, 5.08].map((x) => (
                <Segmento key={x} a={{ x: x + c, y: 0 }} b={{ x: x + c, y: ymax }}
                  px={px} classe="reta auxiliar" />
              ))}
              {ini.length > 1 && (
                <path className="serie repeticao-serie-inicial" data-curva="inicial" d={traco(ini, px)} />
              )}
              {ini.map((p, i) => {
                const q = px(p);
                return <circle key={`i${i}`} className="ponto repeticao-ponto-inicial"
                  data-serie="inicial" cx={q.x} cy={q.y} r={4.5} />;
              })}
              {pts.length > 1 && (
                <path className={marca ? 'serie repeticao-serie-atual' : 'serie'}
                  data-curva={marca} d={traco(pts, px)} />
              )}
              {pts.map((p, i) => {
                const q = px(p);
                return <circle key={i} className="ponto" data-serie={marca}
                  cx={q.x} cy={q.y} r={3.5} />;
              })}
            </>
          )}
        </Plano>
        <p className="fe-legenda">
          {inicial && <span><i className="repeticao-l-inicial" />inicial (fora da conta)</span>}
          {inicial && <span><i className="l-aux" />curva inicial</span>}
          {marca && <span><i className="repeticao-l-atual" />atual</span>}
          <span><i className={marca ? 'repeticao-l-reta-atual' : 'l-reta'} />
            {marca ? 'leituras da repetição' : 'leituras'}</span>
          <span><i className="l-aux" />2,54 e 5,08 mm{c ? ` (+ c = ${num(c, 3)} mm)` : ''}</span>
        </p>
      </div>
    </div>
  );
}

// ── 3 · o ISC adotado ─────────────────────────────────────────────────

function Resultado({
  d, id, gravar, salvando, campoErro, gente,
}: {
  d: EnsaioIsc; id: string; gravar: GravarFn; salvando: string | null;
  campoErro(x: string): string | undefined;
  gente: { id: string; nome: string }[];
}) {
  const c = d.cabecalho;
  const [valor, setValor] = useState('');
  const [criterio, setCriterio] = useState('');
  const [quem, setQuem] = useState<string | null>(null);

  useEffect(() => {
    setValor(c?.isc_adotado != null ? paraCampo(c.isc_adotado) : '');
    setCriterio(c?.criterio_projeto ?? '');
    setQuem(c?.isc_adotado_por ?? null);
  }, [c?.isc_adotado, c?.criterio_projeto, c?.isc_adotado_por]);

  const vivos = d.corpos.filter((x: any) => !x.descartado);
  const falta = !valor ? 'Falta o valor adotado.'
    : criterio.trim().length < 10 ? 'O critério de projeto tem pelo menos 10 caracteres.'
      : !quem ? 'Falta quem adota.' : null;

  return (
    <>
      <BlocoDaFicha titulo="ISC de cada corpo de prova"
        sub="O ISC do CP é o maior entre 2,54 e 5,08 mm (Tabela 4, NOTA) — as duas colunas ficam à vista para a regra ser conferida.">
        {vivos.length === 0
          ? <p className="sub fe-miolo">Nenhum corpo de prova válido ainda.</p>
          : (
            <table className="dados fe-lanc">
              <caption className="so-leitor">ISC de cada corpo de prova</caption>
              <thead>
                <tr><th>CP</th><th className="num">w (%)</th><th className="num">γd (g/cm³)</th>
                  <th className="num">ISC 2,54 (%)</th><th className="num">ISC 5,08 (%)</th>
                  <th className="num">ISC (%)</th><th className="num">Expansão (%)</th></tr>
              </thead>
              <tbody>
                {vivos.map((x: any) => (
                  <tr key={x.id}>
                    <td>{x.ordem}</td>
                    <td className="num">{num(x.umidade_media, 1)}</td>
                    <td className="num">{num(x.massa_especifica_seca, 3)}</td>
                    <td className="num">{num(x.isc_254, CASAS.isc)}</td>
                    <td className="num">{num(x.isc_508, CASAS.isc)}</td>
                    <td className="num"><b>{num(x.isc, CASAS.isc)}</b></td>
                    <td className="num">{num(x.expansao_percent, CASAS.expansao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </BlocoDaFicha>

      {/*
        * O bloco que exige assinatura.
        *
        * Não há botão «usar o da umidade ótima»: seria o sistema
        * escolhendo o critério de projeto por baixo do pano. O número
        * está na faixa para ser lido; quem o adota escreve por quê.
        */}
      <BlocoDaFicha titulo="O ISC do ensaio é uma decisão de projeto"
        sub="A ABNT NBR 9895:2016, 6.4.2, determina que o valor seja obtido da curva segundo critérios de projeto. O laboratório calcula os corpos de prova; quem adota o valor do ensaio assina por ele.">
        <div className="fe-miolo fe-campos">
          <Campo rotulo="ISC adotado (%)" inputMode="decimal" value={valor}
            onChange={(e) => setValor(e.target.value)}
            erro={campoErro('iscAdotado')} />
          <Escolha rotulo="Adotado por" valor={quem} aoEscolher={setQuem}
            opcoes={gente.map((g) => ({ valor: g.id, texto: g.nome }))} />
          <Campo rotulo="Critério de projeto" value={criterio}
            onChange={(e) => setCriterio(e.target.value)}
            erro={campoErro('criterioProjeto')}
            dica="Mínimo de 10 caracteres. Vai impresso no laudo." />
        </div>
        <div className="acoes fe-acoes">
          {falta && <p className="o-que-falta">{falta}</p>}
          <button type="button" className="principal"
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={() => {
              if (falta || salvando !== null) return;
              void gravar('adotado', () =>
                adotarIsc(id, {
                  iscAdotado: Number(paraNumero(valor) ?? 0),
                  criterioProjeto: criterio,
                  iscAdotadoPor: quem,
                }));
            }}>
            {salvando === 'adotado' ? 'Gravando…' : 'Adotar o ISC do ensaio'}
          </button>
        </div>
      </BlocoDaFicha>
    </>
  );
}
